import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getConsultMoreDetails,
  relinquishConsult,
  pastSession,
  getCompleteDropdownData,
  submitCompleteForm,
  getSessionDocument,
  uploadSessionDocument,
  sessionDownloadDocument,
  sessionDeleteDocument,
  submitCancelForm,
  getCancelConsultMetadata,
  assigneeChangeAlert,
} from '../services/consultMoreDetailsService';
import type {
  CompleteConsult,
  MoreDetailsData,
  DocType,
  DocumentId,
  CancelConsultMetadataResponse,
  CancelReasonParams,
  AssigneeChangeAlertResponse,
} from '../services/consultMoreDetailsService';
import type { SuccessResponse } from '../services/loginService';
import { REFRESH_INTERVAL } from '@qler-view/shared_lib/src/shared/constants/CommanConstants';

export interface DdlOption {
  id: number;
  name: string;
}

export interface CompleteConsultParams {
  userId: number;
  consultId: number;
  billable: boolean;
  diagnosis: DdlOption[];
  otherDiagnosis: string;
  recommendation: number;
  emCodes: number;
}

export const useGetConsultMoreDetails = (userId: number, consultId: number) => {
  const { data, isLoading, isError, error, refetch, isSuccess } = useQuery({
    queryKey: ['ConsultMoreDetails', userId, consultId],
    staleTime: 0,
    gcTime: 0,

    queryFn: async () => {
      try {
        const data = await getConsultMoreDetails(userId, consultId);
        return data;
      } catch (error) {
        console.error('Error fetching consult details:', error); // Log error
        throw error;
      }
    },
    // Force refetch when caseId changes
    refetchOnMount: true,
    // Ensure it refetches when window regains focus
    refetchOnWindowFocus: true,
    refetchInterval: REFRESH_INTERVAL,
  });

  console.log('useGetConsultMoreDetails state:', {
    data,
    isLoading,
    isError,
    error,
    isSuccess,
  });

  return {
    consultMoreDetails: data,
    isLoading,
    isSuccess,
    isError,
    error,
    refetch,
  };
};

export const useRelinquishConsult = (
  userId: number,
  onSuccess: () => void,
  onError?: (error: unknown) => void
) => {
  const queryClient = useQueryClient();
  return useMutation<
    MoreDetailsData,
    unknown,
    { userId: number; consultId: number }
  >({
    mutationFn: async ({ userId, consultId }) => {
      const data = await relinquishConsult(userId, consultId);
      return data;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['todaysConsultList', userId],
      });
      if (onSuccess) {
        onSuccess();
      }
    },

    onError: (error) => {
      console.error('Error relinquishing consult:', error);
      if (onError) {
        onError(error);
      }
    },
  });
};

export const usePastSession = (userId: number, consultId: number) => {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['PastSessionList', userId, consultId],
    queryFn: async () => {
      const data = await pastSession(userId, consultId);
      return data;
    },
    refetchInterval: REFRESH_INTERVAL,
  });
  return {
    PastSessionList: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};

export const useCompleteDDLDetails = () => {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['CompleteDDLDetails'],
    queryFn: async () => {
      const data = await getCompleteDropdownData();
      return data;
    },
  });
  return {
    completeDDLDetails: data,
    isLoading,
    isError,
    error,
  };
};

export const useCompleteConsult = () => {
  return useMutation<CompleteConsult, unknown, CompleteConsultParams>({
    mutationFn: async (params) => {
      const data = await submitCompleteForm(params.userId, params.consultId, {
        billable: params.billable,
        diagnosis: params.diagnosis.map((d) => d.id),
        otherDiagnosis: params.otherDiagnosis,
        recommendation: params.recommendation,
        emCodes: params.emCodes,
      });
      return data;
    },

    onSuccess: (data) => {
      console.log('Complete a Consult successfully:', data);
    },
  });
};

export const useUploadSessionDocument = (refetch: () => void) => {
  const queryClient = useQueryClient();
  return useMutation<SuccessResponse, Error, DocType>({
    mutationFn: async (user) => {
      const data = await uploadSessionDocument(user);
      return data;
    },
    onSuccess: (data) => {
      console.log('uploaded successfully', data);

      // Invalidate all queries related to today's consults
      queryClient.invalidateQueries({
        queryKey: ['SessionDocumentList'],
      });
      refetch();
    },
    onError: (error) => {
      console.error('Error accepting consult:', error);
    },
  });
};
export const useGetSessionDocuments = (userId: number, consultId: number) => {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['SessionDocumentList', userId, consultId],
    queryFn: async () => {
      const data = await getSessionDocument(consultId);
      return data;
    },
    refetchInterval: REFRESH_INTERVAL,
  });
  return {
    SessionDocumentList: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};
export const useDownloadDocument = () => {
  return useMutation<string, Error, DocumentId>({
    mutationFn: async (docID) => {
      const data = await sessionDownloadDocument(docID.documentId);
      return data;
    },
  });
};
export const useDeleteDocument = (refetch: () => void) => {
  const queryClient = useQueryClient();
  return useMutation<SuccessResponse, Error, DocumentId>({
    mutationFn: async (docID) => {
      const data = await sessionDeleteDocument(docID.documentId);
      return data;
    },
    onSuccess: (data) => {
      console.log('uploaded successfully', data);

      // Invalidate all queries related to today's consults
      queryClient.invalidateQueries({
        queryKey: ['SessionDocumentList'],
      });
      refetch();
    },
  });
};

export const useCancelConsult = (consultId: number) => {
  return useMutation<CancelReasonParams, unknown, CancelReasonParams>({
    mutationFn: async (params) => {
      const data = await submitCancelForm(consultId, {
        cancelReasonId: params.cancelReasonId,
        technicalDifficultiesId: params.technicalDifficultiesId,
        otherReason: params.otherReason,
      });
      return data;
    },

    onSuccess: (data) => {
      console.log('Cancel Consult successfully:', data);
    },
  });
};

export const useCancelConsultMetadata = () => {
  const { data, isLoading, isError, error, refetch } =
    useQuery<CancelConsultMetadataResponse>({
      queryKey: ['CancelConsultMetadata'],
      queryFn: async () => {
        const data = await getCancelConsultMetadata();
        return data;
      },
      staleTime: 0,
      gcTime: 0,
      enabled: true,
    });

  return {
    CancelConsultMetadata: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};

export const useAssigneeChangeAlert = (userId: number, consultId: number) => {
  const { data, refetch } = useQuery<AssigneeChangeAlertResponse>({
    queryKey: ['AssigneeChangeAlert', userId, consultId],
    queryFn: async () => {
      const response = await assigneeChangeAlert(userId, consultId);
      console.log('AssigneeChangeAlert', response);
      return response;
    },
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  return {
    assigneeChangeAlertData: data,
    refetch,
  };
};
